import { useState, type JSX } from 'react';
import { Link } from 'react-router';
import { Badge, Button, Card, Search, Select, SelectItem } from '@viniciomendesr/rayrovisk-design-system';
import { useDeletePin, usePins } from '../api/queries';
import { AccessStateScreen, accessStateOf } from '../access/AccessState';
import { CopyToClipboard } from '../components/CopyToClipboard';
import type { Pin, PinKind } from '../api/types';

/** The word on the origin badge and in the filter — never the English `PinKind`. */
const ORIGIN_LABEL: Record<PinKind, string> = {
  library: 'Biblioteca',
  assembled: 'Prompt montado',
  script: 'Roteiro'
};

/** How many badges beyond the origin one a card shows before folding the rest into a count. */
const MAX_EXTRA_BADGES = 2;

/** Screen 09. Everything here came from somewhere she chose to keep — biblioteca, montador ou roteiro. */
export function PinsScreen(): JSX.Element {
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('all');
  const [order, setOrder] = useState('recent');
  const pins = usePins(query, kind, order);
  const remove = useDeletePin();

  const state = accessStateOf(pins.error);
  if (state) {
    return <AccessStateScreen state={state} />;
  }

  const rows = pins.data?.pins ?? [];

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 1000 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* "Fixados" rather than "Meus prompts": the navigation calls it that,
            and two names for one list is one name too many. Same rows, same
            table — only the word changed. */}
        <h1 style={{ margin: 0, font: '400 32px/1.12 var(--rvk-font-expressive)' }}>Fixados</h1>
        <p
          style={{
            margin: 0,
            font: '400 14.5px/1.6 var(--rvk-font-functional)',
            color: 'var(--rvk-text-secondary)'
          }}
        >
          Ficam na sua conta. Sair e trocar de aparelho não perde nada.
        </p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 240px', minWidth: 0, maxWidth: 420 }}>
          <Search
            label="Buscar nos seus fixados"
            placeholder="nicho, encomenda, bastidor…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onClear={() => setQuery('')}
          />
        </div>
        <div style={{ flex: '0 1 190px', minWidth: 170 }}>
          <Select aria-label="Filtrar por origem" value={kind} onValueChange={setKind}>
            <SelectItem value="all">Todas as origens</SelectItem>
            <SelectItem value="library">Biblioteca</SelectItem>
            <SelectItem value="assembled">Prompt montado</SelectItem>
            <SelectItem value="script">Roteiro</SelectItem>
          </Select>
        </div>
        <div style={{ flex: '0 1 180px', minWidth: 150 }}>
          <Select aria-label="Ordenar os fixados" value={order} onValueChange={setOrder}>
            <SelectItem value="recent">Mais recentes</SelectItem>
            <SelectItem value="oldest">Mais antigos</SelectItem>
          </Select>
        </div>
        <span
          style={{
            font: '400 12.5px/1.4 var(--rvk-font-functional)',
            color: 'var(--rvk-text-secondary)',
            marginLeft: 'auto'
          }}
        >
          {rows.length} {rows.length === 1 ? 'fixado' : 'fixados'}
        </span>
      </div>

      {pins.isLoading && (
        <p style={{ font: '400 13px/1.5 var(--rvk-font-functional)', color: 'var(--rvk-text-tertiary)' }}>
          Carregando…
        </p>
      )}

      {remove.isError && (
        <p style={{ margin: 0, font: '400 13px/1.5 var(--rvk-font-functional)' }}>
          {(remove.error as Error).message}
        </p>
      )}

      {!pins.isLoading && rows.length === 0 && (
        <Card layer="functional" padding="lg">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ font: '400 19px/1.2 var(--rvk-font-expressive)' }}>
              {query === '' ? 'Você ainda não fixou nada.' : 'Nada com esse termo.'}
            </span>
            <span
              style={{
                font: '400 13.5px/1.5 var(--rvk-font-functional)',
                color: 'var(--rvk-text-secondary)'
              }}
            >
              {query === ''
                ? // Três portas para o primeiro fixado agora, não uma: biblioteca,
                  // montador e roteiro carregam o mesmo botão de fixar. A frase
                  // aponta a mais simples das três em vez de listar as outras duas.
                  'Fixe um prompt da Biblioteca, ou gere um no montador: ele fica aqui, na sua conta.'
                : 'Tente outra palavra da tarefa que você descreveu.'}
            </span>
          </div>
        </Card>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows.map((pin) => (
          <PinCard
            key={pin.id}
            pin={pin}
            removing={remove.isPending}
            onRemove={() => void remove.mutateAsync(pin.id).catch(() => undefined)}
          />
        ))}
      </div>
    </section>
  );
}

function PinCard({
  pin,
  removing,
  onRemove
}: {
  pin: Pin;
  removing: boolean;
  onRemove: () => void;
}): JSX.Element {
  // Deduplicated: `caption` is both a format and an objective, and the two
  // render the same word, so that pair drew "Legenda" twice.
  const extra = [
    ...new Set(
      [pin.format?.label, pin.objective?.label, ...pin.adjustments.map((a) => a.label)].filter(
        (label): label is string => Boolean(label)
      )
    )
  ];
  const shown = extra.slice(0, MAX_EXTRA_BADGES);
  const hidden = extra.length - shown.length;

  return (
    <div className="rvk-pin-card">
      <Link to={`/gerador/fixados/${pin.id}`} className="rvk-pin-card-body">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
          <Badge tone="accent" variant="subtle" size="sm">
            {ORIGIN_LABEL[pin.kind]}
          </Badge>
          {shown.map((label, index) => (
            <Badge key={index} tone="neutral" variant="outline" size="sm">
              {label}
            </Badge>
          ))}
          {hidden > 0 && (
            <Badge tone="neutral" variant="subtle" size="sm">
              +{hidden}
            </Badge>
          )}
          <span
            style={{
              flex: 'none',
              marginLeft: 'auto',
              font: '400 12px/1.4 var(--rvk-font-functional)',
              color: 'var(--rvk-text-tertiary)'
            }}
          >
            {relative(pin.createdAt)}
          </span>
        </div>
        <span className="rvk-pin-card-label">
          {pin.label}
        </span>
      </Link>
      <div className="rvk-pin-card-actions">
        <CopyToClipboard
          text={pin.body}
          surface="pin-list"
          sourceRef={pin.kind}
          label="Copiar"
          variant="secondary"
          size="sm"
          confirmation="in-button"
        />
        <Button variant="ghost" size="sm" loading={removing} onClick={onRemove}>
          Remover
        </Button>
      </div>
    </div>
  );
}

/** Coarse on purpose: "3 dias" is what she needs, not a timestamp. */
function relative(iso: string): string {
  const days = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
  if (days <= 0) {
    return 'hoje';
  }
  if (days === 1) {
    return 'ontem';
  }
  if (days < 7) {
    return `${days} dias`;
  }
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return weeks === 1 ? '1 semana' : `${weeks} semanas`;
  }
  return `${Math.floor(days / 30)} meses`;
}
